// Core
import React, { useState, useEffect } from 'react';

// Libraries
import { Menu as MenuAntD } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Location,
  NavigateFunction,
  useLocation,
  useNavigate,
} from 'react-router-dom';

// Store
import {
  getUser,
  setAuthToken,
  setCustomer,
  setUser,
} from '../../store/session.store';

// Types
import { User, UserType } from '../../types/global';

// Config
import { BREAKPOINTS } from '../../config/screen';

// Components
import { Input } from '../elements/Input';
import { LinkTo } from '../elements/LinkTo';
import { Logo, LogoSize } from '../elements/Logo';
import {
  Options,
  OptionsDropdown,
  OptionsDropdownColor,
  OptionsDropdownPlacement,
} from '../elements/OptionsDropdown';
import {
  Account,
  BackToCustomer,
  CaretDown,
  Close,
  Hamburger,
  LogoutBox,
  Search,
  Settings,
} from '../icons';

// Component
import {
  NavigationContextType,
  MainNavigationProps,
} from './MainNavigation.types';
import {
  Actions,
  AvatarContainer,
  CaretDownContainer,
  Layout,
  LeftSection,
  MenuButton,
  MobileMenuBody,
  MobileMenuCloseButton,
  MobileMenuContainer,
  MobileMenuFooter,
  MobileMenuFooterButton,
  MobileMenuHeader,
  NavigationContainer,
  OptionsDropdownContainer,
  RightSection,
  SearchContainer,
  UserActions,
} from './MainNavigation.style';

function MainNavigation({
  className,
  context = NavigationContextType.customer,
  searchComponentEnabled = false,
  testId,
}: MainNavigationProps) {
  /* ***********************************************************************************************
  **************************************** DEPENDENCIES ********************************************
  *********************************************************************************************** */

  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const location: Location = useLocation();

  /* ***********************************************************************************************
  ******************************************* STATE ************************************************
  *********************************************************************************************** */

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  /* *********************************************************************************************
  ****************************************** METHODS *********************************************
  ********************************************************************************************* */

  type Item = {
    key: string;
    label: string;
  };

  // Get user
  const user: User | null = getUser();

  // Function of logout
  const logoutUser = (): void => {
    setAuthToken(null);
    setUser(null);
    setCustomer(null);
    navigate('/');
    setIsMobileMenuOpen(false); // Close mobile menu when logging out
  };

  const primaryOptions: Options[] = user ? [
    {
      text: user.email,
      color: OptionsDropdownColor.normal,
      disabled: false,
      onClick: () => {},
      disabledHover: true,
    },
  ] : [];

  // Function wrapper for mobile menu navigation
  const handleMobileNavigate = (path: string): void => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const secondaryOptions: Options[] = [
    ...(user?.type === UserType.admin
      || user?.type === UserType.service
    ) && location.pathname !== '/'
      ? [{
        text: t('common.customers'),
        icon: <BackToCustomer />,
        color: OptionsDropdownColor.normal,
        disabled: false,
        onClick: () => handleMobileNavigate('/'),
      }]
      : [],
    ...(!location.pathname.startsWith('/settings'))
      ? [{
        text: t('common.settings'),
        icon: <Settings />,
        color: OptionsDropdownColor.normal,
        disabled: false,
        onClick: () => handleMobileNavigate('/settings'),
      }]
      : [],
    {
      text: t('common.logout'),
      icon: <LogoutBox />,
      color: OptionsDropdownColor.normal,
      disabled: false,
      onClick: logoutUser,
    },
  ];

  // Context-based navigation items
  const items: Item[] = context === NavigationContextType.admin
    ? [
      { key: '/', label: t('common.customers') },
      ...(user?.type === UserType.admin
        ? [{ key: '/equipment-models', label: t('common.equipmentModel.plural') }]
        : []),
      { key: '/inventory', label: t('common.inventory') },
      // @TODO: AdminActions has no actions implemented
      // ...(user?.type === UserType.admin
      //   ? [{ key: '/admin-actions', label: t('common.admin.singular') }]
      //   : []),
    ]
    : [
      { key: '/plant-management', label: t('common.plantManagement') },
    ];

  // Convert selected key
  const convertSelectedKeyToAntD = (path: string): string => {
    const foundItem: Item | undefined = items.find(
      (item: Item) => path === item.key,
    );
    return foundItem ? foundItem.key : '';
  };

  // Function of menu redirect
  const menuRedirectToPath = (path: string): void => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  /* ***********************************************************************************************
  **************************************** COMPONENT HANDLING **************************************
  *********************************************************************************************** */

  useEffect((): void | (() => void) => {
    if (isMobileMenuOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'visible';
      document.body.style.overflow = 'visible';
    }

    return (): void => {
      document.documentElement.style.overflow = 'visible';
      document.body.style.overflow = 'visible';
    };
  }, [isMobileMenuOpen]);

  // Close mobile menu when resizing the screen to a size >= sm
  useEffect((): void | (() => void) => {
    const handleResize = (): void => {
      if (window.innerWidth >= BREAKPOINTS.md) { // breakpoint media.sm
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);

    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <Layout
        data-testid={testId}
        className={className}
      >
        <NavigationContainer>
          <div>
            <LeftSection>
              <LinkTo target={user?.type === UserType.customer ? '/plant-management' : '/'}>
                <Logo size={LogoSize.xs} />
              </LinkTo>
              {
                searchComponentEnabled && (
                  <SearchContainer>
                    <Input // @TODO apply search functionality later
                      onChange={(value: string | number | null | undefined) => (value as string)}
                      value=""
                      placeholder={`${t('layout.mainNavigation.search')}`}
                      prefix={<Search />}
                    />
                  </SearchContainer>
                )
              }
            </LeftSection>
          </div>
          <div>
            <RightSection>
              <Actions>
                <MenuAntD
                  mode="horizontal"
                  items={items}
                  onClick={(e) => menuRedirectToPath(e.key)}
                  selectedKeys={[convertSelectedKeyToAntD(location.pathname)]}
                  disabledOverflow
                />
                <UserActions>
                  <MenuButton
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    type="button"
                  >
                    <Hamburger />
                  </MenuButton>
                  <OptionsDropdownContainer>
                    <OptionsDropdown
                      options={primaryOptions}
                      secondaryOptions={secondaryOptions}
                      placement={OptionsDropdownPlacement.bottomLeft}
                      className="options-dropdown"
                    >
                      <AvatarContainer>
                        <Account />
                      </AvatarContainer>
                      <CaretDownContainer>
                        <CaretDown />
                      </CaretDownContainer>
                    </OptionsDropdown>
                  </OptionsDropdownContainer>
                </UserActions>
              </Actions>
            </RightSection>
          </div>
        </NavigationContainer>
      </Layout>

      <MobileMenuContainer
        data-testid="mobile-menu-container"
        isOpen={isMobileMenuOpen}
      >
        <div>
          <MobileMenuHeader>
            <LinkTo target={user?.type === UserType.customer ? '/plant-management' : '/'}>
              <Logo size={LogoSize.xs} />
            </LinkTo>
            <MobileMenuCloseButton
              data-testid="mobile-menu-close-button"
              onClick={() => setIsMobileMenuOpen(false)}
              type="button"
            >
              <Close />
            </MobileMenuCloseButton>
          </MobileMenuHeader>
          <MobileMenuBody>
            <MenuAntD
              mode="vertical"
              items={items}
              onClick={(e) => menuRedirectToPath(e.key)}
              selectedKeys={[convertSelectedKeyToAntD(location.pathname)]}
            />
          </MobileMenuBody>
        </div>
        <MobileMenuFooter>
          {secondaryOptions.map((option: Options) => (
            <MobileMenuFooterButton
              key={option.text}
              onClick={option.onClick}
              disabled={option.disabled}
              type="button"
            >
              {option.icon}
              <span>{option.text}</span>
            </MobileMenuFooterButton>
          ))}
        </MobileMenuFooter>
      </MobileMenuContainer>
    </>
  );
}

export { MainNavigation };
